// Políticas de inventario de la organización (RES-26): forma del formulario y diff contra lo
// guardado. Sin dependencias de React para poder testearlo puro.
//
// Solo se editan acá las políticas que hoy tienen efecto real. Las que quedan para épicas
// siguientes (agotado automático, catálogo/recetas bloqueados) las guarda y devuelve la API, pero
// no se muestran: un control que no cambia nada es peor que no tenerlo.

/** Políticas de la organización con control en la UI, en el orden en que se muestran. */
export const ORG_POLICY_FIELDS = [
  'deductionTrigger',
  'restockOnCancel',
  'allowNegativeStock',
  'costingMethod',
  'expirationWarningDays',
];

/** Opciones de los desplegables (el backend las devuelve en `options`; esto es el fallback). */
export const DEDUCTION_TRIGGERS = ['order_created', 'order_accepted', 'order_delivered'];
export const COSTING_METHODS = ['average', 'last'];
export const ALERT_CHANNELS = ['admin_push', 'email'];
export const ALERT_ROLES = ['owner', 'admin', 'staff'];

/**
 * Convierte la respuesta de `GET /inventory/settings` en el estado del formulario.
 * @param {Object} view - Respuesta del endpoint
 * @returns {Object} Estado del formulario
 */
export function toSettingsForm(view) {
  const effective = (view && view.effective) || {};
  const alert = effective.lowStockAlert || {};
  const sources = (view && view.sources) || {};
  return {
    deductionTrigger: effective.deductionTrigger || 'order_created',
    restockOnCancel: effective.restockOnCancel !== false,
    allowNegativeStock: effective.allowNegativeStock !== false,
    costingMethod: effective.costingMethod || 'average',
    expirationWarningDays:
      effective.expirationWarningDays === undefined ? 3 : String(effective.expirationWarningDays),
    // La alerta es la única política que el local puede pisar
    alertInherited: sources.lowStockAlert !== 'restaurant',
    alertChannels: Array.isArray(alert.channels) ? [...alert.channels] : ['admin_push'],
    alertRoles: Array.isArray(alert.roles) ? [...alert.roles] : ['owner', 'admin'],
  };
}

/**
 * Políticas de la organización que cambiaron respecto de lo efectivo cargado.
 * @param {Object} form - Estado del formulario
 * @param {Object} view - Respuesta cargada
 * @returns {Object|null} Cambios para `PUT /inventory/settings` o `null` si no hay
 */
export function buildOrgSettingsChanges(form, view) {
  const base = toSettingsForm(view);
  const changes = {};
  for (const key of ORG_POLICY_FIELDS) {
    if (key === 'expirationWarningDays') {
      const current = parseInt(form[key], 10);
      if (Number.isFinite(current) && current !== parseInt(base[key], 10)) changes[key] = current;
      continue;
    }
    if (form[key] !== base[key]) changes[key] = form[key];
  }
  // La alerta se define a nivel organización mientras el local la herede
  if (form.alertInherited && !sameAlert(form, base)) {
    changes.lowStockAlert = { channels: [...form.alertChannels], roles: [...form.alertRoles] };
  }
  return Object.keys(changes).length > 0 ? changes : null;
}

/**
 * Cambio del override del local: el objeto de alerta, `null` para volver a heredar, o nada.
 * @param {Object} form - Estado del formulario
 * @param {Object} view - Respuesta cargada
 * @returns {Object|null} Cuerpo para `PUT /inventory/settings/restaurant` o `null`
 */
export function buildLocalSettingsChange(form, view) {
  const base = toSettingsForm(view);
  if (form.alertInherited) {
    // Volver a heredar solo tiene sentido si había override
    return base.alertInherited ? null : { lowStockAlert: null };
  }
  if (base.alertInherited || !sameAlert(form, base)) {
    return { lowStockAlert: { channels: [...form.alertChannels], roles: [...form.alertRoles] } };
  }
  return null;
}

/**
 * Compara los canales y roles de la alerta entre dos estados del formulario.
 * @param {Object} a - Estado
 * @param {Object} b - Estado
 * @returns {boolean} `true` si son iguales
 */
function sameAlert(a, b) {
  const norm = (list) => [...(list || [])].sort().join(',');
  return (
    norm(a.alertChannels) === norm(b.alertChannels) && norm(a.alertRoles) === norm(b.alertRoles)
  );
}

/**
 * Indica si el formulario tiene cambios sin guardar.
 * @param {Object} form - Estado del formulario
 * @param {Object} view - Respuesta cargada
 * @returns {boolean} `true` si hay cambios
 */
export function isSettingsDirty(form, view) {
  return Boolean(buildOrgSettingsChanges(form, view) || buildLocalSettingsChange(form, view));
}

/**
 * Valida el formulario antes de guardar.
 * @param {Object} form - Estado del formulario
 * @returns {{ok: boolean, field?: string}} Resultado
 */
export function validateSettingsForm(form) {
  const days = parseInt(form.expirationWarningDays, 10);
  if (!Number.isFinite(days) || days < 0 || days > 60) {
    return { ok: false, field: 'expirationWarningDays' };
  }
  if (!form.alertChannels.length) return { ok: false, field: 'alertChannels' };
  if (!form.alertRoles.length) return { ok: false, field: 'alertRoles' };
  return { ok: true };
}
